import React from "react";
import PageBanner from "../components/PageBanner";
import Sidebar from "../components/Sidebar";
import { PAGE_BANNERS, SITE } from "../data/content";
import { Phone, Printer, MapPin, Clock } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../lib/api";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  function onChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill in your name, email and a message." });
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast({
        title: "Message received \u2014 thank you!",
        description: "We will be in touch soon. For appointments, please also call (218) 728-7010 x 203.",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: msg });
    } finally {
      setSubmitting(false);
    }
  }

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    SITE.address2 + ", " + SITE.address3,
  )}&output=embed`;

  return (
    <div>
      <PageBanner src={PAGE_BANNERS.contact} alt="Contact" fallbackKey="contact" />
      <div className="flex flex-col lg:flex-row gap-8">
        <Sidebar />
        <article className="flex-1 nn-content">
          <h1>Nourish Natural Health Clinic, LLC</h1>
          <p className="nn-italic-tagline -mt-2 mb-6">Located Within the Duluth Hub</p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#7d8e54] mt-0.5" />
                <div>
                  <p>{SITE.address2}</p>
                  <p>{SITE.address3}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#7d8e54]" />
                <p>T: {SITE.phone}</p>
              </div>
              <div className="flex items-center gap-3">
                <Printer className="w-5 h-5 text-[#7d8e54]" />
                <p>F: {SITE.fax}</p>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#7d8e54] mt-0.5" />
                <div>
                  {SITE.hours.map((h) => (
                    <p key={h.day}>
                      {h.day} {h.time}
                    </p>
                  ))}
                  <p className="italic text-sm">{SITE.hoursNote}</p>
                </div>
              </div>
            </div>

            <div className="aspect-[4/3] w-full border border-[#d6dcb6]">
              <iframe
                title="Clinic location map"
                src={mapSrc}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <h2>Send Us a Message</h2>
          <p>
            For appointments please call us at <strong>(218) 728-7010 x 203</strong> to schedule
            your visit. Or use the form below and we will get back to you.
          </p>
          <form onSubmit={onSubmit} className="space-y-3 max-w-xl !mt-4">
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Your name *"
              className="w-full border border-[#d6dcb6] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#8da55f]"
            />
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              placeholder="Email *"
              className="w-full border border-[#d6dcb6] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#8da55f]"
            />
            <input
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="Phone (optional)"
              className="w-full border border-[#d6dcb6] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#8da55f]"
            />
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              placeholder="How can we help? *"
              rows={5}
              className="w-full border border-[#d6dcb6] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#8da55f]"
            />
            <button
              type="submit"
              disabled={submitting}
              className="nn-sage-dark px-6 py-2 font-display tracking-wider hover:bg-[#5b6a37] transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </article>
      </div>
    </div>
  );
}
